import { Link } from 'react-router-dom'
import { IconType } from 'react-icons'
import { MdTrendingUp, MdTrendingDown, MdArrowForward } from 'react-icons/md'

interface StatCardProps {
    title: string
    value: number
    subtitle: string
    growth?: number
    icon: IconType
    color: string
    href?: string
    format?: 'number' | 'currency'
}

export default function StatCard({
    title,
    value,
    subtitle,
    growth,
    icon: Icon,
    color,
    href,
    format = 'number',
}: StatCardProps) {
    const formattedValue =
        format === 'currency'
            ? new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
              }).format(value / 100)
            : new Intl.NumberFormat('pt-BR').format(value)

    const content = (
        <div
            className={`group relative overflow-hidden rounded-xl bg-gradient-to-br from-base-100 to-base-200/50 border border-base-300/50 hover:border-${color}/30 transition-all duration-300 hover:shadow-xl hover:-translate-y-1`}
        >
            <div className="absolute top-0 right-0 w-24 h-24 opacity-5">
                <Icon className="text-[6rem] -rotate-12" />
            </div>

            <div className="relative p-5 flex flex-col gap-3">
                <div className="flex items-start justify-between">
                    <div className={`p-2.5 rounded-xl bg-${color}/10`}>
                        <Icon className={`text-xl text-${color}`} />
                    </div>

                    {growth !== undefined && (
                        <div
                            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                growth >= 0
                                    ? 'bg-success/10 text-success'
                                    : 'bg-error/10 text-error'
                            }`}
                        >
                            {growth >= 0 ? (
                                <MdTrendingUp className="text-sm" />
                            ) : (
                                <MdTrendingDown className="text-sm" />
                            )}
                            <span>{Math.abs(growth)}%</span>
                        </div>
                    )}
                </div>

                <div>
                    <p className="text-xs font-medium text-base-content/50 uppercase tracking-wide">
                        {title}
                    </p>
                    <p className="text-2xl font-bold text-base-content mt-1">
                        {formattedValue}
                    </p>
                    <p className="text-xs text-base-content/60 mt-1">{subtitle}</p>
                </div>

                {href && (
                    <div className="flex justify-end">
                        <MdArrowForward className="text-base-content/30 group-hover:text-base-content/60 transition-colors" />
                    </div>
                )}
            </div>
        </div>
    )

    if (href) {
        return <Link to={href}>{content}</Link>
    }

    return content
}
